import { Button, DatePicker, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DateIcon from "../../../assets/icons/DateIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import RequestDetailsModal from "../../../components/SupportTickets/RequestDetailsModal";
import CustomTable from "../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetSupportListQuery } from "../../../features/supportTicket/supportTicketApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatUTCDate } from "../../../lib/time";
import { GetSupportParamsType, GetSupportResponseType } from "../../../types";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectSearchOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import { useSupportListReportMutation } from "../../../features/report/reportApi";

const { RangePicker } = DatePicker;

enum Status {
  open = "status-danger",
  closed = "status-success",
}

export default function SupportTickets() {
  const { open, handleClose, handleOpen } = useModal<
    string,
    "REPORT_REQUEST"
  >();
  let [queryParams, setSearchParams] = useSearchParams();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    keyword,
    handleFilter,
    handleSearch,
  } = useTable<GetSupportParamsType>({
    defaultFilter: {
      status: "",
      search_type: "title",
      start_date: moment().startOf("month").format(dbDateFormat),
      end_date: moment().endOf("month").format(dbDateFormat),
    },
  });
  const { isLoading, isError, error, data } = useGetSupportListQuery({
    params: {
      ...filter,
      search_keyword: keyword,
      search_type: keyword ? filter?.search_type : undefined,
    },
  });
  const [
    supportListReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useSupportListReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get support data fail");

  const columns: ColumnsType<GetSupportResponseType["data"]["supports"][0]> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 90,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (value) => value || "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: keyof typeof Status) => (
        <span className={Status[value]}>{capitalize(value)}</span>
      ),
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      render: (val) => {
        return val ? formatUTCDate(val) : "n/a";
      },
    },
    {
      title: "Closed at",
      dataIndex: "closed_at",
      render: (val) => (val ? formatUTCDate(val) : "n/a"),
    },
    {
      title: "Assignee",
      dataIndex: "assign_to_info",
      render: (val) =>
        val ? (
          <div>
            <h6 className="font-medium text-body-text">{val?.name}</h6>
            <span>ID: {val?.employee_id || "-"}</span>
          </div>
        ) : (
          "n/a"
        ),
      width: 300,
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      render: (val) => (
        <Button
          className="action-button"
          size="small"
          icon={<ViewIcon />}
          onClick={() =>
            handleOpen({
              type: "VIEW",
              data: val,
            })
          }
        />
      ),
    },
  ];

  useEffect(() => {
    const targetId = queryParams.get("targeId");
    if (targetId) {
      handleOpen({
        type: "VIEW",
        data: targetId,
      });

      setSearchParams({ targeId: "" });
    }
  }, [handleOpen, queryParams, setSearchParams]);

  useEffect(() => {
    const checkReload = () => {
      const entries = performance.getEntriesByType("navigation");
      if (
        entries.length > 0 &&
        (entries[0] as PerformanceNavigationTiming).type === "reload"
      ) {
        setSearchParams({ targeId: "" });
      }
    };

    checkReload();
  }, [setSearchParams]);

  return (
    <SidebarLayout title="Support Tickets">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total_supports}
        dataSource={addKeyInArray(data?.data?.supports)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <div className="flex items-center gap-4">
            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              value={filter?.status}
              options={status}
              onChange={(val) => handleFilter("status", val)}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              showSearch
              placeholder="Search Type"
              value={filter.search_type}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("search_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              popupMatchSelectWidth={false}
            />
            <Input
              key={filter.search_type}
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                filter.search_type || "",
                true
              )}`}
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />
            <RangePicker
              format={dateFormat}
              style={{ width: 260 }}
              suffixIcon={<DateIcon />}
              value={
                filter?.start_date && filter?.end_date
                  ? [
                      dayjs(filter?.start_date, dbDateFormat),
                      dayjs(filter?.end_date, dbDateFormat),
                    ]
                  : null
              }
              onChange={(e) => {
                handleFilter("start_date", e?.[0]?.format(dbDateFormat));
                handleFilter("end_date", e?.[1]?.format(dbDateFormat));
              }}
            />
            <Button
              onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
              className="download-button"
              icon={<DownloadIcon />}
            >
              Report Requests
            </Button>
          </div>
        }
      />

      {open.type === "VIEW" && (
        <RequestDetailsModal
          open={open.open}
          data={open?.data}
          handleClose={() => {
            setSearchParams({ targeId: "" });
            handleClose();
          }}
        />
      )}

      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            supportListReport({
              body: {
                name: "Support details",
              },
              params: {
                ...filter,
                search_keyword: keyword,
                search_type: keyword ? filter?.search_type : undefined,
                ...params,
                skip: 0,
                limit: 0,
              },
            });
          }}
        />
      )}
    </SidebarLayout>
  );
}

const status = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Open",
    value: "open",
  },
  {
    label: "Closed",
    value: "closed",
  },
];

const searchTypeSelect = [
  {
    label: "Title",
    value: "title",
  },
  {
    label: "Support ID",
    value: "support_id",
  },
  {
    label: "Assignee Name",
    value: "assignee_name",
  },
];
